/**
 * useSeo — per-route SEO head management for the BrontoY SPA.
 *
 * React SPAs render everything client-side, so the `<head>` tags we
 * ship in `public/index.html` are the SAME for every route by default.
 * Google's crawler DOES execute JS, but for canonicals / OpenGraph /
 * per-page titles to work correctly we still need to mutate the head
 * synchronously as the user navigates.  This hook does exactly that,
 * without adding a react-helmet-async dependency.
 *
 * The canonical origin is intentionally HARD-CODED to the production
 * domain `https://brontoy.com`.  Canonicals are the ONE place a hard-
 * coded URL is not just OK but *required* — they identify the single
 * "official" URL for a page across every deployment (sandbox, staging,
 * preview, production).  Google collapses duplicates to the canonical
 * so we always point back to brontoy.com regardless of which host is
 * currently serving the SPA.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const CANONICAL_ORIGIN = "https://brontoy.com";
const DEFAULT_TITLE = "BrontoY — By Bronto Intelligence";
const DEFAULT_DESCRIPTION =
  "Share Moments. Express Views. BrontoY is a social platform by Bronto Intelligence for creators, thinkers and communities.";
const DEFAULT_IMAGE = `${CANONICAL_ORIGIN}/favicon-512x512.png`;

/** Upsert `<meta name="foo" content="bar">` — creates the node if missing. */
function upsertMetaName(name, content) {
  if (!name) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content == null ? "" : String(content));
}

/** Upsert `<meta property="og:foo" content="bar">`. */
function upsertMetaProperty(property, content) {
  if (!property) return;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content == null ? "" : String(content));
}

/** Upsert `<link rel="canonical" href="…">`. */
function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * @param {object} opts
 * @param {string} [opts.title]        Full document title.  Falls back to default.
 * @param {string} [opts.description]  Meta description (~160 char sweet spot).
 * @param {string} [opts.canonicalPath] Absolute path (must start with `/`).
 *                                       Defaults to the current pathname.
 * @param {string} [opts.image]        Absolute image URL for OG/Twitter previews.
 * @param {boolean}[opts.noindex]      Emit `<meta name="robots" content="noindex,nofollow">`.
 * @param {string} [opts.ogType]       Defaults to "website".
 */
export function useSeo({
  title,
  description,
  canonicalPath,
  image,
  noindex = false,
  ogType = "website",
} = {}) {
  const location = useLocation();
  const path = canonicalPath || location.pathname || "/";
  const canonicalUrl = `${CANONICAL_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
  const finalTitle = title || DEFAULT_TITLE;
  const finalDescription = description || DEFAULT_DESCRIPTION;
  const finalImage = image || DEFAULT_IMAGE;

  useEffect(() => {
    // Title
    if (document.title !== finalTitle) document.title = finalTitle;
    // Description
    upsertMetaName("description", finalDescription);
    // Canonical (Google's canonical / duplicate-content signal)
    upsertCanonical(canonicalUrl);
    // Robots
    if (noindex) upsertMetaName("robots", "noindex,nofollow");
    else upsertMetaName("robots", "index,follow");
    // Open Graph — controls the preview card shown on Facebook, iMessage,
    // Slack, LinkedIn, Discord, Threads, etc.  All 4 fields are required
    // for a rich preview.
    upsertMetaProperty("og:type", ogType);
    upsertMetaProperty("og:site_name", "BrontoY");
    upsertMetaProperty("og:title", finalTitle);
    upsertMetaProperty("og:description", finalDescription);
    upsertMetaProperty("og:url", canonicalUrl);
    upsertMetaProperty("og:image", finalImage);
    // Twitter — Twitter/X still reads its own tag namespace.
    upsertMetaName("twitter:card", "summary_large_image");
    upsertMetaName("twitter:title", finalTitle);
    upsertMetaName("twitter:description", finalDescription);
    upsertMetaName("twitter:image", finalImage);
  }, [finalTitle, finalDescription, canonicalUrl, finalImage, noindex, ogType]);
}

/**
 * <Seo /> — a JSX-friendly wrapper for `useSeo`.  Use it inside any
 * page component; it renders nothing to the DOM.
 *
 *   <Seo
 *     title="Login — BrontoY"
 *     description="Sign in to BrontoY."
 *     canonicalPath="/login"
 *   />
 */
export function Seo(props) {
  useSeo(props);
  return null;
}

export const CANONICAL_ORIGIN_URL = CANONICAL_ORIGIN;
